import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  User, 
  Users, 
  Share2, 
  CreditCard, 
  Receipt, 
  MessageCircle, // TODO: Used when Messages feature is enabled
  Bell, // TODO: Used when Notifications feature is enabled
  Package, // TODO: Used when Shipments feature is enabled
  Star,
  Shield, 
  Lock, 
  HelpCircle,
  ChevronRight,
  X
} from 'lucide-react';

const ProfileSidebar = ({ activeSection, onSectionChange, onClose = null }) => {
  const [expandedSections, setExpandedSections] = useState({});
  const { user } = useAuth();
  const displayName = user?.name || 'Creator';

  const menuItems = [
    {
      id: 'my-collabs',
      label: 'My Collabs',
      icon: Users,
      path: '/dashboard/collabs'
    },
    {
      id: 'my-profile',
      label: 'My Profile',
      icon: User,
      path: '/dashboard/profile'
    },
    {
      id: 'social-media',
      label: 'Social Media',
      icon: Share2,
      path: '/dashboard/social-media'
    }
    /* Subscriptions Section - DISABLED */
    /* Temporarily disabled - can be re-enabled in the future */
    /*
    ,{
      id: 'subscriptions',
      label: 'Subscriptions',
      icon: CreditCard,
      path: '/dashboard/subscriptions'
    }
    */
    /* Billing Section - DISABLED */
    /* Temporarily disabled - can be re-enabled in the future */
    /*
    ,{
      id: 'billing',
      label: 'Billing',
      icon: Receipt,
      path: '/dashboard/billing',
      children: [
        { id: 'billing-history', label: 'Payment History', path: '/dashboard/billing/history' },
        { id: 'billing-methods', label: 'Payment Methods', path: '/dashboard/billing/methods' },
        { id: 'billing-invoices', label: 'Invoices', path: '/dashboard/billing/invoices' }
      ]
    }
    */
    ,
    // TODO: Enable Messages section when feature is implemented
    // {
    //   id: 'messages',
    //   label: 'Messages',
    //   icon: MessageCircle,
    //   path: '/dashboard/messages',
    //   badge: 3
    // },
    // TODO: Enable Notifications section when feature is implemented
    // {
    //   id: 'notifications',
    //   label: 'Notifications',
    //   icon: Bell,
    //   path: '/dashboard/notifications',
    //   badge: 5
    // },
    // TODO: Enable Shipments section when feature is implemented
    // {
    //   id: 'shipments',
    //   label: 'Shipments',
    //   icon: Package,
    //   path: '/dashboard/shipments'
    // },
    {
      id: 'reviews',
      label: 'Reviews',
      icon: Star,
      path: '/dashboard/reviews'
    },
    {
      id: 'security',
      label: 'Security',
      icon: Shield,
      path: '/dashboard/security'
    }
    /* Privacy Section - DISABLED */
    /* Temporarily disabled - can be re-enabled in the future */
    /*
    ,{
      id: 'privacy',
      label: 'Privacy',
      icon: Lock,
      path: '/dashboard/privacy'
    }
    */
    /* Support Section - DISABLED */
    /* Temporarily disabled - can be re-enabled in the future */
    /*
    ,{
      id: 'support',
      label: 'Support',
      icon: HelpCircle,
      path: '/dashboard/support',
      children: [
        { id: 'help-center', label: 'Help Center', path: '/dashboard/support/help' },
        { id: 'contact-us', label: 'Contact Us', path: '/dashboard/support/contact' },
        { id: 'report-issue', label: 'Report Issue', path: '/dashboard/support/report' }
      ]
    }
    */
  ];

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleItemClick = (item) => {
    if (item.children) {
      toggleSection(item.id);
    } else {
      onSectionChange(item.id, item.path);
    }
  };

  const handleChildClick = (parentId, child) => {
    onSectionChange(child.id, child.path);
  };

  return (
    <div className="w-64 bg-white border-r border-gray-200 h-full overflow-y-auto flex flex-col">
      {/* Mobile Close Button */}
      <div className="lg:hidden flex items-center justify-between p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        )}
      </div>

      {/* User Profile Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-collaborb-band4 to-collaborb-spherical-highlight flex items-center justify-center">
            <User className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-medium text-gray-900">{displayName}</p>
            <p className="text-sm text-gray-500">Creator</p>
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="p-2 flex-1">
        {menuItems.map((item) => (
          <div key={item.id} className="mb-1">
            <button
              onClick={() => handleItemClick(item)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left hover:bg-gray-50 transition-colors ${
                activeSection === item.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
              }`}
            >
              <div className="flex items-center space-x-3">
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
                {item.badge && (
                  <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5 min-w-[20px] text-center">
                    {item.badge}
                  </span>
                )}
              </div>
              {item.children && (
                <ChevronRight 
                  className={`w-4 h-4 transition-transform ${
                    expandedSections[item.id] ? 'rotate-90' : ''
                  }`} 
                />
              )}
            </button>

            {/* Submenu */}
            {item.children && expandedSections[item.id] && (
              <div className="ml-6 mt-1 space-y-1">
                {item.children.map((child) => (
                  <button
                    key={child.id}
                    onClick={() => handleChildClick(item.id, child)}
                    className={`w-full flex items-center px-3 py-2 rounded-lg text-left text-sm hover:bg-gray-50 transition-colors ${
                      activeSection === child.id ? 'bg-blue-50 text-blue-700' : 'text-gray-600'
                    }`}
                  >
                    {child.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 mt-auto">
        <div className="text-xs text-gray-500 text-center">
          <p className="font-medium">CollabOrb</p>
          <p>Profile Management</p>
          <p className="mt-1 text-gray-400">v2.0.1</p>
        </div>
      </div>
    </div>
  );
};

export default ProfileSidebar;
